import React from 'react'
import { BrowserRouter as Router, Switch, Route, Redirect } from 'react-router-dom'
import CircularProgress from '@material-ui/core/CircularProgress'
import { createMuiTheme, MuiThemeProvider } from '@material-ui/core/styles'
import { CssBaseline } from '@material-ui/core'

import { auth } from './config/firebase'
import Auth from './components/Auth'
import MenuAppBar from './components/MenuAppBar'
import Home from './views/Home/index'
import Campaign from './views/Campaign/index'

const PrivateRoute = ({ component: Component, isAuthenticated, user, ...rest }) => {
  return (
    <Route
      {...rest}
      render={(props) => isAuthenticated === true
        ? <Component user={user} {...props} />
        : <Redirect to={{ pathname: '/auth', state: { from: props.location } }} />}
    />
  )
}

const App = ({ isDarkMode }) => {
  const [ isDarkModeEnabled, setIsDarkModeEnabled ] = React.useState(isDarkMode)

  const theme = React.useMemo(
    () => {
      const type = isDarkModeEnabled ? 'dark' : 'light'
      const theme = createMuiTheme({
        palette: {
          type
        }
      })
      return theme
    },
    [ isDarkModeEnabled ],
  )

  const [ isLoading, setIsLoading ] = React.useState(true)
  // We'll use existence of user to tell if we're authenticated or not
  const [ user, setUser ] = React.useState({})

  React.useEffect(() => {
    // This runs on log out as well!
    const unsubscribe = auth.onAuthStateChanged((auth) => {
      const isLoggedOut = auth === null
      setUser(isLoggedOut ? {} : auth)
      if (isLoggedOut) {
        console.log('Clearing local storage values')
        window.localStorage.removeItem('swroller-darkmode')
        window.localStorage.removeItem('swroller-paper-info')
        window.localStorage.removeItem('swroller-paper-character')
        window.localStorage.removeItem('swroller-paper-skills')
        window.localStorage.removeItem('swroller-paper-notepad')
        window.localStorage.removeItem('swroller-notepad-text')
        console.log('Cleared local storage values')
      }
      setIsLoading(false)
    })

    return () => {
      unsubscribe()
    }
  }, [])

  const isAuthenticated = Object.keys(user).length > 0
  const baseProps = { isAuthenticated, user }

  const renderAuth = React.useCallback((props) => {
    if (!isAuthenticated) return <Auth {...props} {...props} />

    const { location: { state } } = props
    let to = '/'
    if (state && state.referrer) to = state.referrer
    else if (state && state.from && state.from.pathname) to = state.from.pathname

    return <Redirect to={to} />
  }, [ isAuthenticated ])

  return (
    <MuiThemeProvider theme={theme}>
      <CssBaseline />
      <Router>
        <MenuAppBar
          isDarkModeEnabled={isDarkModeEnabled}
          setIsDarkModeEnabled={setIsDarkModeEnabled}
          email={user.email}
          isAuthenticated={isAuthenticated} />

        {isLoading ?
          <div style={{
            width: '100%',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            marginTop: 40
          }}>
            <CircularProgress />
          </div>
          :
          <Switch>
            <Route exact path='/auth' render={renderAuth} {...baseProps} />
            <PrivateRoute path='/campaign/:campaignId' component={Campaign} {...baseProps} />
            <PrivateRoute path='/' component={Home} {...baseProps} />
          </Switch>
        }
      </Router>
    </MuiThemeProvider>
  )
}

export default App
